import React, { useCallback, useEffect, useState } from 'react';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  Grid,
  IconButton,
  InputLabel,
  LinearProgress,
  MenuItem,
  Select,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import { alpha } from '@mui/material/styles';
import {
  Add as AddIcon,
  Delete as DeleteIcon,
  ExpandMore as ExpandMoreIcon,
  AutoAwesome as AiIcon,
} from '@mui/icons-material';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip as RechartsTooltip, ResponsiveContainer } from 'recharts';
import { NutritionService } from '../services/nutritionService';
import type { DailySummary, HistoryEntry, LogMealInput, MealLogEntry } from '../services/nutritionService';

const MEAL_TYPE_LABELS: Record<string, string> = {
  breakfast: 'Закуска',
  lunch: 'Обяд',
  dinner: 'Вечеря',
  snack: 'Снак',
  other: 'Друго',
};

const MEAL_TYPE_ORDER = ['breakfast', 'lunch', 'dinner', 'snack', 'other'];

interface AddMealDialogProps {
  open: boolean;
  onClose: () => void;
  onSave: (meal: LogMealInput) => Promise<void>;
}

function AddMealDialog({ open, onClose, onSave }: AddMealDialogProps) {
  const [mealName, setMealName] = useState('');
  const [calories, setCalories] = useState('');
  const [proteinG, setProteinG] = useState('');
  const [carbsG, setCarbsG] = useState('');
  const [fatsG, setFatsG] = useState('');
  const [mealType, setMealType] = useState('lunch');
  const [estimating, setEstimating] = useState(false);
  const [saving, setSaving] = useState(false);
  const [aiError, setAiError] = useState<string | null>(null);

  const handleEstimate = async () => {
    if (!mealName.trim()) return;
    try {
      setEstimating(true);
      setAiError(null);
      const result = await NutritionService.estimateCalories(mealName);
      setCalories(String(Math.round(result.calories)));
      setProteinG(String(Math.round(result.protein_g)));
      setCarbsG(String(Math.round(result.carbs_g)));
      setFatsG(String(Math.round(result.fats_g)));
    } catch {
      setAiError('AI оценката не е достъпна. Въведете стойностите ръчно.');
    } finally {
      setEstimating(false);
    }
  };

  const handleSubmit = async () => {
    try {
      setSaving(true);
      await onSave({
        meal_name: mealName,
        calories: Number(calories) || 0,
        protein_g: Number(proteinG) || 0,
        carbs_g: Number(carbsG) || 0,
        fats_g: Number(fatsG) || 0,
        meal_type: mealType || undefined,
      });
      // Reset
      setMealName('');
      setCalories('');
      setProteinG('');
      setCarbsG('');
      setFatsG('');
      setMealType('lunch');
      setAiError(null);
      onClose();
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>Добави хранене</DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ mt: 1 }}>
          <TextField
            label="Описание на ястието"
            value={mealName}
            onChange={(e) => setMealName(e.target.value)}
            fullWidth
            placeholder="напр. 200г пилешко с ориз"
            inputProps={{ maxLength: 255 }}
          />
          <Button
            variant="outlined"
            startIcon={estimating ? <CircularProgress size={16} /> : <AiIcon />}
            onClick={handleEstimate}
            disabled={!mealName.trim() || estimating}
          >
            Оцени с AI
          </Button>
          {aiError && <Alert severity="warning">{aiError}</Alert>}
          <Grid container spacing={2}>
            <Grid size={6}>
              <TextField
                label="Калории"
                value={calories}
                onChange={(e) => setCalories(e.target.value)}
                type="number"
                fullWidth
                inputProps={{ min: 0 }}
              />
            </Grid>
            <Grid size={6}>
              <TextField
                label="Протеин (г)"
                value={proteinG}
                onChange={(e) => setProteinG(e.target.value)}
                type="number"
                fullWidth
                inputProps={{ min: 0 }}
              />
            </Grid>
            <Grid size={6}>
              <TextField
                label="Въглехидрати (г)"
                value={carbsG}
                onChange={(e) => setCarbsG(e.target.value)}
                type="number"
                fullWidth
                inputProps={{ min: 0 }}
              />
            </Grid>
            <Grid size={6}>
              <TextField
                label="Мазнини (г)"
                value={fatsG}
                onChange={(e) => setFatsG(e.target.value)}
                type="number"
                fullWidth
                inputProps={{ min: 0 }}
              />
            </Grid>
          </Grid>
          <FormControl fullWidth>
            <InputLabel>Тип хранене</InputLabel>
            <Select
              value={mealType}
              label="Тип хранене"
              onChange={(e) => setMealType(e.target.value)}
            >
              <MenuItem value="breakfast">Закуска</MenuItem>
              <MenuItem value="lunch">Обяд</MenuItem>
              <MenuItem value="dinner">Вечеря</MenuItem>
              <MenuItem value="snack">Снак</MenuItem>
            </Select>
          </FormControl>
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={saving}>Отказ</Button>
        <Button
          variant="contained"
          onClick={handleSubmit}
          disabled={!mealName.trim() || saving}
          startIcon={saving ? <CircularProgress size={16} color="inherit" /> : undefined}
        >
          Запази
        </Button>
      </DialogActions>
    </Dialog>
  );
}

function CalorieRing({ consumed, target }: { consumed: number; target: number }) {
  const progress = target > 0 ? Math.min((consumed / target) * 100, 100) : 0;
  const remaining = Math.max(0, target - consumed);

  return (
    <Box sx={{ position: 'relative', display: 'inline-flex', width: 140, height: 140 }}>
      <CircularProgress
        variant="determinate"
        value={100}
        size={140}
        thickness={6}
        sx={{ color: alpha('#7dd3fc', 0.15), position: 'absolute' }}
      />
      <CircularProgress
        variant="determinate"
        value={progress}
        size={140}
        thickness={6}
        sx={{
          color: consumed > target && target > 0 ? 'error.main' : 'primary.main',
        }}
      />
      <Box
        sx={{
          top: 0,
          left: 0,
          bottom: 0,
          right: 0,
          position: 'absolute',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        <Typography variant="h5" fontWeight={700}>{Math.round(consumed)}</Typography>
        <Typography variant="caption" color="text.secondary">
          {target > 0 ? `/ ${target} kcal` : 'kcal'}
        </Typography>
        {target > 0 && (
          <Typography variant="caption" color="text.secondary">
            {remaining > 0 ? `${Math.round(remaining)} оставащи` : 'Цел достигната'}
          </Typography>
        )}
      </Box>
    </Box>
  );
}

function MacroBar({ label, value, color }: { label: string; value: number; color: string }) {
  return (
    <Box sx={{ flex: 1 }}>
      <Stack direction="row" justifyContent="space-between" sx={{ mb: 0.5 }}>
        <Typography variant="caption" fontWeight={600}>{label}</Typography>
        <Typography variant="caption">{Math.round(value)}г</Typography>
      </Stack>
      <LinearProgress
        variant="determinate"
        value={Math.min(100, value > 0 ? 100 : 0)}
        sx={{
          height: 8,
          borderRadius: 4,
          backgroundColor: alpha(color, 0.15),
          '& .MuiLinearProgress-bar': { backgroundColor: color, borderRadius: 4 },
        }}
      />
    </Box>
  );
}

const NutritionPage: React.FC = () => {
  const [summary, setSummary] = useState<DailySummary | null>(null);
  const [history, setHistory] = useState<HistoryEntry[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [calorieTarget, setCalorieTarget] = useState(2000);

  const loadData = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const [summaryData, historyData, profileData] = await Promise.all([
        NutritionService.getDailySummary(),
        NutritionService.getHistory(7),
        NutritionService.getNutritionistProfile().catch(() => null),
      ]);
      setSummary(summaryData);
      setHistory(historyData);
      if (profileData?.dailyCalorieTarget) {
        setCalorieTarget(profileData.dailyCalorieTarget);
      }
    } catch {
      setError('Неуспешно зареждане на данните за хранене.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const handleAddMeal = async (meal: LogMealInput) => {
    await NutritionService.logMeal(meal);
    await loadData();
  };

  const handleDeleteMeal = async (id: number) => {
    try {
      await NutritionService.deleteMeal(id);
      await loadData();
    } catch {
      setError('Неуспешно изтриване на записа.');
    }
  };

  // Group meals by type
  const mealsByType: Record<string, MealLogEntry[]> = {};
  if (summary) {
    for (const meal of summary.meals) {
      const type = meal.meal_type || 'other';
      if (!mealsByType[type]) mealsByType[type] = [];
      mealsByType[type].push(meal);
    }
  }

  const historyChartData = [...history].reverse().map((entry) => ({
    date: entry.date.substring(5), // MM-DD
    calories: Math.round(entry.calories),
  }));

  if (loading && !summary) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {/* Daily overview */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Stack
            direction={{ xs: 'column', sm: 'row' }}
            spacing={3}
            alignItems="center"
          >
            <CalorieRing
              consumed={summary?.totals.calories ?? 0}
              target={calorieTarget}
            />
            <Box sx={{ flex: 1, width: '100%' }}>
              <Typography variant="h6" fontWeight={700} gutterBottom>
                Дневен преглед
              </Typography>
              <Stack direction="row" spacing={2}>
                <MacroBar label="Протеин" value={summary?.totals.protein_g ?? 0} color="#22c55e" />
                <MacroBar label="Въглехидрати" value={summary?.totals.carbs_g ?? 0} color="#f59e0b" />
                <MacroBar label="Мазнини" value={summary?.totals.fats_g ?? 0} color="#ef4444" />
              </Stack>
              <Button
                variant="contained"
                startIcon={<AddIcon />}
                onClick={() => setDialogOpen(true)}
                sx={{ mt: 2 }}
              >
                Добави хранене
              </Button>
            </Box>
          </Stack>
        </CardContent>
      </Card>

      {/* Meals list grouped by type */}
      <Typography variant="h6" fontWeight={700} sx={{ mb: 1.5 }}>
        Ястия за деня
      </Typography>
      {summary && summary.meals.length === 0 ? (
        <Alert severity="info" sx={{ mb: 3 }}>
          Все още няма логнати ястия за днес.
        </Alert>
      ) : (
        <Box sx={{ mb: 3 }}>
          {MEAL_TYPE_ORDER.filter((type) => mealsByType[type]?.length > 0).map((type) => (
            <Accordion key={type} defaultExpanded>
              <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                <Stack direction="row" spacing={1} alignItems="center">
                  <Typography fontWeight={700}>
                    {MEAL_TYPE_LABELS[type] || type}
                  </Typography>
                  <Chip
                    label={`${Math.round(mealsByType[type].reduce((sum, m) => sum + m.calories, 0))} kcal`}
                    size="small"
                    variant="outlined"
                  />
                </Stack>
              </AccordionSummary>
              <AccordionDetails>
                <Stack spacing={1}>
                  {mealsByType[type].map((meal) => (
                    <Card key={meal.id} variant="outlined">
                      <CardContent sx={{ py: 1, '&:last-child': { pb: 1 } }}>
                        <Stack direction="row" alignItems="center" justifyContent="space-between">
                          <Box>
                            <Typography variant="body2" fontWeight={600}>
                              {meal.meal_name}
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                              {Math.round(meal.calories)} kcal &middot; П: {Math.round(meal.protein_g)}г &middot; В: {Math.round(meal.carbs_g)}г &middot; М: {Math.round(meal.fats_g)}г
                            </Typography>
                          </Box>
                          <Tooltip title="Изтрий">
                            <IconButton
                              size="small"
                              onClick={() => handleDeleteMeal(meal.id)}
                              color="error"
                            >
                              <DeleteIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        </Stack>
                      </CardContent>
                    </Card>
                  ))}
                </Stack>
              </AccordionDetails>
            </Accordion>
          ))}
        </Box>
      )}

      {/* History chart */}
      {historyChartData.length > 0 && (
        <>
          <Typography variant="h6" fontWeight={700} sx={{ mb: 1.5 }}>
            Калории — последните 7 дни
          </Typography>
          <Card sx={{ mb: 3 }}>
            <CardContent>
              <ResponsiveContainer width="100%" height={220}>
                <BarChart data={historyChartData}>
                  <CartesianGrid strokeDasharray="3 3" stroke={alpha('#fff', 0.1)} />
                  <XAxis dataKey="date" tick={{ fontSize: 12 }} />
                  <YAxis tick={{ fontSize: 12 }} />
                  <RechartsTooltip
                    contentStyle={{ backgroundColor: '#1e293b', border: 'none', borderRadius: 8 }}
                    labelStyle={{ color: '#94a3b8' }}
                  />
                  <Bar dataKey="calories" fill="#7dd3fc" radius={[4, 4, 0, 0]} name="Калории" />
                </BarChart>
              </ResponsiveContainer>
            </CardContent>
          </Card>
        </>
      )}

      <AddMealDialog
        open={dialogOpen}
        onClose={() => setDialogOpen(false)}
        onSave={handleAddMeal}
      />
    </Box>
  );
};

export default NutritionPage;
